/**
 * useDesignSystem — Central hook that reads all design settings from localStorage
 * and applies them to the live site (CSS variables, font, body classes, etc.)
 * 
 * Used in App.tsx so every page benefits automatically.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  loadDesignSettings, loadBrandSettings, loadIdentitySettings,
  DesignSettings, BrandSettings, IdentitySettings,
  DESIGN_SETTINGS_KEY, BRAND_SETTINGS_KEY, IDENTITY_SETTINGS_KEY,
} from '@/pages/admin/components/design/designTypes';
import { COLOR_PRESETS, applyColorPreset } from '@/hooks/useStoreSettings';
import { FONT_OPTIONS, RADIUS_OPTIONS, FOOTER_STYLES, PAGE_BG_PRESETS } from '@/pages/admin/components/design/designConstants';

export interface ActiveDesign {
  design: DesignSettings;
  brand: BrandSettings;
  identity: IdentitySettings;
}

const STORAGE_EVENT = 'ebdaa_design_changed';

/** Dispatch a custom event so all tabs/components react instantly */
export function notifyDesignChanged() {
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT));
}

function applyDesignToDOM(design: DesignSettings, brand: BrandSettings, identity: IdentitySettings) {
  const root = document.documentElement;

  // ── Color preset ──────────────────────────────────────────────────────────
  const preset = COLOR_PRESETS.find(p => p.id === design.colorPresetId) ?? COLOR_PRESETS[0];
  applyColorPreset(preset);

  // ── Font ──────────────────────────────────────────────────────────────────
  const font = FONT_OPTIONS.find(f => f.id === design.fontId) ?? FONT_OPTIONS[0];
  root.style.setProperty('--font-primary', font.css);
  // Apply to body so all text inherits
  document.body.style.fontFamily = font.css;

  // ── Border radius ─────────────────────────────────────────────────────────
  const radius = RADIUS_OPTIONS.find(r => r.id === design.radiusId) ?? RADIUS_OPTIONS[2];
  root.style.setProperty('--radius-base', radius.value);

  // ── Button style ──────────────────────────────────────────────────────────
  root.style.setProperty('--button-style', design.buttonStyleId);

  // ── Card style ────────────────────────────────────────────────────────────
  root.style.setProperty('--card-style', design.cardStyleId);

  // ── Animations ────────────────────────────────────────────────────────────
  if (!design.animationsEnabled) {
    root.style.setProperty('--animation-duration', '0s');
    root.classList.add('no-animations');
  } else {
    root.style.removeProperty('--animation-duration');
    root.classList.remove('no-animations');
  }

  // ── Dark mode ─────────────────────────────────────────────────────────────
  if (design.darkMode) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  // ── Footer color ──────────────────────────────────────────────────────────
  const footerStyle = FOOTER_STYLES.find(f => f.id === brand.footerStyle) ?? FOOTER_STYLES[0];
  root.style.setProperty('--footer-bg', footerStyle.bg);
  root.style.setProperty('--footer-text', footerStyle.text);

  // ── Navbar style ──────────────────────────────────────────────────────────
  root.style.setProperty('--navbar-style', brand.navbarStyle);
  root.style.setProperty('--navbar-height', brand.navbarHeight === 'sm' ? '56px' : brand.navbarHeight === 'lg' ? '88px' : '72px');

  // ── Hero settings ─────────────────────────────────────────────────────────
  root.style.setProperty('--hero-overlay', `${identity.heroOverlayOpacity / 100}`);

  // ── Background pattern ────────────────────────────────────────────────────
  root.style.setProperty('--bg-pattern', identity.bgPattern ?? 'none');

  // ── Product grid cols ─────────────────────────────────────────────────────
  root.style.setProperty('--product-grid-cols', String(design.productGridCols ?? 4));

  // ── Page background color ─────────────────────────────────────────────────
  const bgPreset = PAGE_BG_PRESETS.find(b => b.id === (design.pageBgId ?? 'black')) ?? PAGE_BG_PRESETS[0];
  const pageBg = design.pageBgId === 'custom'
    ? (design.pageBgCustomColor || '#0a0a0a')
    : bgPreset.color;
  root.style.setProperty('--page-bg', pageBg);
}

export function useDesignSystem(): ActiveDesign {
  const [state, setState] = useState<ActiveDesign>(() => ({
    design: loadDesignSettings(),
    brand: loadBrandSettings(),
    identity: loadIdentitySettings(),
  }));

  const reload = useCallback(() => {
    const design = loadDesignSettings();
    const brand = loadBrandSettings();
    const identity = loadIdentitySettings();
    applyDesignToDOM(design, brand, identity);
    setState({ design, brand, identity });
  }, []);

  // Apply on mount
  useEffect(() => {
    reload();
  }, [reload]);

  // Listen for changes from admin panel (same tab via custom event)
  useEffect(() => {
    window.addEventListener(STORAGE_EVENT, reload);
    // Also listen for cross-tab changes via storage event
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === DESIGN_SETTINGS_KEY ||
        e.key === BRAND_SETTINGS_KEY ||
        e.key === IDENTITY_SETTINGS_KEY
      ) {
        reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(STORAGE_EVENT, reload);
      window.removeEventListener('storage', onStorage);
    };
  }, [reload]);

  return state;
}

/** Lightweight hook — just reads current design state without applying to DOM */
export function useDesignState(): ActiveDesign {
  const [state, setState] = useState<ActiveDesign>(() => ({
    design: loadDesignSettings(),
    brand: loadBrandSettings(),
    identity: loadIdentitySettings(),
  }));

  useEffect(() => {
    const reload = () => {
      setState({
        design: loadDesignSettings(),
        brand: loadBrandSettings(),
        identity: loadIdentitySettings(),
      });
    };
    window.addEventListener(STORAGE_EVENT, reload);
    window.addEventListener('storage', reload);
    return () => {
      window.removeEventListener(STORAGE_EVENT, reload);
      window.removeEventListener('storage', reload);
    };
  }, []);

  return state;
}
